import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Filter, CreditCard, BarChart3, UsersRound, Settings as SettingsIcon,
  MessageCircle, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "../tabon/Logo";
import { useSAAuth } from "../../contexts/SAAuthContext";

const LINKS = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/venues", label: "Venue Directory", icon: Building2 },
  { to: "/admin/pipeline", label: "Onboarding Pipeline", icon: Filter },
  { to: "/admin/billing", label: "Billing Overview", icon: CreditCard },
  { to: "/admin/analytics", label: "Platform Analytics", icon: BarChart3 },
  { to: "/admin/team", label: "Team Management", icon: UsersRound },
  { to: "/admin/config", label: "Configuration", icon: SettingsIcon },
  { to: "/admin/template-health", label: "Template Health", icon: MessageCircle },
];

export function SALayout({ children }) {
  const { user, logout } = useSAAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const Nav = ({ onNav }) => (
    <nav className="flex flex-col gap-1">
      {LINKS.map((l) => {
        const Icon = l.icon;
        const active = location.pathname.startsWith(l.to);
        return (
          <Link
            key={l.to}
            to={l.to}
            onClick={onNav}
            data-testid={`sa-nav-${l.to.split("/").pop()}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
              active
                ? "bg-[#9D00FF] text-white shadow-lg shadow-[#9D00FF]/20"
                : "text-white/80 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Icon size={18} /> <span className="truncate">{l.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#2D1B4E] border-r border-white/10 p-5 fixed inset-y-0 left-0 z-30">
        <div className="flex items-center gap-2 mb-8">
          <Logo size="sm" />
          <span
            data-testid="sa-badge"
            className="text-[10px] font-bold uppercase tracking-wider bg-white/10 text-[#A0A0A0] border border-white/15 rounded-full px-2 py-0.5"
          >
            Superadmin
          </span>
        </div>
        <Nav />
        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="text-xs text-white/70 mb-2">
            <div className="font-semibold text-white truncate">{user?.name}</div>
            <div className="flex items-center gap-2">
              <span>{user?.email}</span>
              <span className="text-[9px] bg-[#9D00FF]/30 border border-[#9D00FF]/50 rounded px-1.5 py-0.5 uppercase tracking-wider">
                {user?.role}
              </span>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="sa-logout"
            className="flex items-center gap-2 text-xs text-white/70 hover:text-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#2D1B4E] border-b border-white/10 flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <Logo size="sm" />
          <span className="text-[10px] font-bold uppercase tracking-wider bg-white/10 text-[#A0A0A0] border border-white/15 rounded-full px-2 py-0.5">
            Superadmin
          </span>
        </div>
        <button onClick={() => setOpen(true)} className="p-2 border border-white/10 rounded-lg" data-testid="sa-mobile-menu">
          <Menu size={18} />
        </button>
      </header>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden bg-black/70 backdrop-blur-sm">
          <div className="absolute right-0 top-0 h-full w-72 bg-[#2D1B4E] p-5 flex flex-col gap-2">
            <div className="flex items-center justify-between mb-4">
              <Logo size="sm" />
              <button onClick={() => setOpen(false)} className="p-2 border border-white/10 rounded-lg"><X size={18} /></button>
            </div>
            <Nav onNav={() => setOpen(false)} />
            <button onClick={logout} className="mt-auto flex items-center gap-2 text-sm text-white/80 hover:text-white">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 min-w-0">
        <div className="px-4 md:px-8 py-8 max-w-[1400px] mx-auto">{children}</div>
        <footer className="px-4 md:px-8 pb-6 pt-4 text-xs text-[#A0A0A0] text-center">
          TabOn Superadmin · Internal Use Only · © 2026 TabOn
        </footer>
      </main>
    </div>
  );
}
